<script setup lang="ts">
import { onErrorCaptured } from "vue";
import { container } from "tsyringe";

// shared
import type { ServerErrorResponse } from "@/shared/libs/http/axios/interface";
import ConfirmLibsError from "@/shared/libs/http/axios/ConfirmAxiosError";
import { usePopupManager } from "@/shared/composables/usePopupManager";
import { AccessStorageImpl } from "@/shared/libs/storage/AccessStorageImpl";

import AlertUnExpectedError from "@/shared/components/popup/AlertUnExpectedError.vue";

// features
import AlertInvalidTokenError from "@/features/authentication/ui/AlertInvalidTokenError.vue";

import type { AlertInvalidTokenErrorProps } from "@/features/authentication/model/interface";

// entities
import { CommonAccountsBehavior } from "@/entities/common/interface/common-accounts/domain/behaviors/CommonAccountsBehavior";

// type
import type { AlertUnExpectedErrorPopupProps } from "@/shared/plugins/interface";

// instances
const confirmLibsError = container.resolve(ConfirmLibsError);
const accessStorage = container.resolve(AccessStorageImpl);
const commonAccountsBehavior = container.resolve(CommonAccountsBehavior);

const { openPopup } = usePopupManager();

/** root error boundary */
onErrorCaptured((error) => {
    if (confirmLibsError.isAxiosError<ServerErrorResponse>(error)) {
        if (error.name === "CanceledError") return false;

        const errorResponse = error.response?.data;

        /** code 가 지정되어있지 않는 에러에 한하여 throw */
        if (!errorResponse?.errorCode) {
            openPopup<AlertUnExpectedErrorPopupProps>({
                component: AlertUnExpectedError,
                props: {
                    closeKey: "",
                    rest: {
                        title: `관리자에게 문의해주세요.`,
                        message: error.message,
                    },
                },
            });
            return false;
        }

        /** accessToken 이상 시  */
        if (commonAccountsBehavior.checkAccessTokenError(errorResponse)) {
            if (accessStorage.getItem("userRefreshToken")) {
                return true;
            }

            openPopup<AlertInvalidTokenErrorProps>({
                component: AlertInvalidTokenError,
                props: {
                    closeKey: "invalid-token-error",
                    rest: {
                        title: "로그인 정보를 갱신해야합니다. 로그인 페이지로 이동합니다.",
                        isStrong: true,
                    },
                },
            });
            return true;
        }

        openPopup<AlertUnExpectedErrorPopupProps>({
            component: AlertUnExpectedError,
            props: {
                closeKey: "",
                rest: {
                    title: `관리자에게 문의해주세요.`,
                    message: error.message,
                },
            },
        });
        return false;
    } else {
        openPopup<AlertUnExpectedErrorPopupProps>({
            component: AlertUnExpectedError,
            props: {
                closeKey: "",
                rest: {
                    title: `관리자에게 문의해주세요.`,
                    message: error.message,
                },
            },
        });
        return false;
    }
});
</script>

<template>
    <div class="flex justify-center items-center w-full h-full">
        <slot></slot>
    </div>
</template>

<style scoped></style>
